// src/App.tsx
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ModalProvider } from './contexts/ModalContext';
import { ToastProvider } from './contexts/ToastContext';
import { ConfirmDialogProvider } from './contexts/ConfirmDialogContext';
import { ObjectiveProvider } from './features/goal/contexts/ObjectiveContext';
import { OnboardingProvider } from './features/onboarding';
import PrivateRoute from './components/auth/PrivateRoute';
import { logPageView } from './utils/analytics';
import { useSyncManager } from './hooks/useSyncManager';
// import AdminRoute from './components/auth/AdminRoute'; // 暫時移除，改用功能級權限控制
// 布局
import AppLayout from './layouts/AppLayout';

// 身份驗證頁面
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ChangePassword from './pages/ChangePassword';
import VerifyEmail from './pages/VerifyEmail';

// 應用主頁面
import Today from './pages/Today';
import Tasks from './features/task/pages/Tasks';
import Calendar from './pages/Calendar';
import Captures from './pages/Captures';
import Profile from './pages/Profile';
import Objectives from './features/goal/pages/GoalsPage';
import GoalDetail from './features/goal/pages/GoalDetailPage';
import Achievements from './pages/Achievements';
import ProfilePasswordChange from './pages/ProfilePasswordChange';
import ProfileSubscription from './pages/ProfileSubscription';
import ProfileIdealWeek from './pages/ProfileIdealWeek';
import Plans from './pages/Plans';

// 特殊頁面
import Feedback from './pages/Feedback';

// 設置流程頁面
import SetupIntro from './pages/setup/SetupIntro';
import SetupStep1 from './pages/setup/SetupStep1';
import SetupStep2 from './pages/setup/SetupStep2';
import SetupStep3 from './pages/setup/SetupStep3';

// 訂閱相關頁面
import SubscriptionSuccess from './pages/subscription/SubscriptionSuccess';
import SubscriptionCancel from './pages/subscription/SubscriptionCancel';

// Admin 頁面 - 使用 lazy loading 隔離 bundle
const AdminShell = React.lazy(() => import('./components/admin/AdminShell'));
const AdminDashboard = React.lazy(() => import('./pages/admin/AdminDashboard'));
const AdminUsers = React.lazy(() => import('./pages/AdminUsers'));
const AdminFeedback = React.lazy(() => import('./pages/AdminFeedback'));


// Analytics 頁面追蹤組件
function AnalyticsTracker() {
  const location = useLocation();

  useEffect(() => {
    // 🛡️ 錯誤邊界：Analytics 失敗不應影響應用運行
    try {
      logPageView(location.pathname);
    } catch (error) {
      // 靜默失敗 - logPageView 內部已有錯誤處理
      // 如果仍拋出錯誤，代表是意外情況，不需額外記錄
    }
  }, [location.pathname]); // ✅ 只監聽 pathname 變化

  return null;
}

// 同步管理組件 - 監聽網路狀態並自動同步
function SyncManager() {
  useSyncManager();
  return null;
}

function App() {
  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true
      }}
    >
      <AuthProvider>
        <ObjectiveProvider>
          <ToastProvider>
            <OnboardingProvider>
              <ConfirmDialogProvider>
                <ModalProvider>
                <AnalyticsTracker />
                <SyncManager />
            <Routes>
            {/* 公開路由 - 身份驗證頁面 */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/change-password" element={<ChangePassword />} />
            <Route path="/public-test" element={<div>This is a public page</div>} />

            {/* 需要登入但不需要完整布局的頁面 */}
            <Route path="/verify-email" element={
              <PrivateRoute>
                <VerifyEmail />
              </PrivateRoute>
            } />
            
            {/* 個人化設置流程 - 介紹頁面和三步驟設定 */}
            <Route path="/setup/intro" element={
              <PrivateRoute skipPersonalizationCheck={true}>
                <SetupIntro />
              </PrivateRoute>
            } />
            <Route path="/setup/step-1" element={
              <PrivateRoute skipPersonalizationCheck={true}>
                <SetupStep1 />
              </PrivateRoute>
            } />
            <Route path="/setup/step-2" element={
              <PrivateRoute skipPersonalizationCheck={true}>
                <SetupStep2 />
              </PrivateRoute>
            } />
            <Route path="/setup/step-3" element={
              <PrivateRoute skipPersonalizationCheck={true}>
                <SetupStep3 />
              </PrivateRoute>
            } />

            {/* 方案選擇頁面 - 需要登入但不需要完整布局 */}
            <Route path="/plans" element={
              <PrivateRoute>
                <Plans />
              </PrivateRoute>
            } />

            {/* 訂閱相關頁面 - 需要登入但不需要完整布局 */}
            <Route path="/subscription/success" element={
              <PrivateRoute>
                <SubscriptionSuccess />
              </PrivateRoute>
            } />
            <Route path="/subscription/cancel" element={
              <PrivateRoute>
                <SubscriptionCancel />
              </PrivateRoute>
            } />

            {/* Admin 管理頁面 - lazy loaded，巢狀路由共享 AdminShell */}
            <Route path="/admin" element={
              <PrivateRoute>
                <React.Suspense fallback={<div className="min-h-screen bg-gray-50" />}>
                  <AdminShell />
                </React.Suspense>
              </PrivateRoute>
            }>
              <Route index element={<AdminDashboard />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="feedback" element={<AdminFeedback />} />
            </Route>

            {/* 受保護的路由 - 使用 AppLayout */}
            <Route path="/" element={
              <PrivateRoute>
                <AppLayout />
              </PrivateRoute>
            }>
              {/* 默認重定向 */}
              <Route index element={<Navigate to="/today" replace />} />

              {/* 主要應用頁面 */}
              <Route path="today" element={<Today />} />
              <Route path="tasks" element={<Tasks />} />
              <Route path="calendar" element={<Calendar />} />
              <Route path="captures" element={<Captures />} />

              {/* 用戶相關頁面 */}
              <Route path="profile" element={<Profile />} />
              <Route path="profile/password-change" element={<ProfilePasswordChange />} />
              <Route path="profile/subscription" element={<ProfileSubscription />} />
              <Route path="profile/ideal-week" element={<ProfileIdealWeek />} />
              <Route path="feedback" element={<Feedback />} />
              <Route path="goals" element={<Objectives />} />
              <Route path="goals/:id" element={<GoalDetail />} />
              <Route path="goals/achievements" element={<Achievements />} />
              <Route path="objectives" element={<Objectives />} />
            </Route>

            {/* 找不到頁面時重定向到登入頁面 */}
            <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
                </ModalProvider>
              </ConfirmDialogProvider>
            </OnboardingProvider>
          </ToastProvider>
        </ObjectiveProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;